/**
 * Helpers for turning raw Gmail messages into readable, threaded conversations.
 *
 * Gmail hands us snippets that are HTML-escaped (`that&#39;s`, `&lt;byron@…&gt;`)
 * and full bodies that still carry the entire quoted reply chain, which is why the
 * raw data reads as entity soup. Everything here is display-only: decode entities,
 * flatten HTML to text, split off the quoted history, and group messages into threads.
 */

const NAMED_ENTITIES = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  hellip: '…',
  mdash: '—',
  ndash: '–',
  lsquo: '‘',
  rsquo: '’',
  ldquo: '“',
  rdquo: '”',
  bull: '•',
  middot: '·',
  laquo: '«',
  raquo: '»',
  trade: '™',
  reg: '®',
  copy: '©',
  deg: '°',
  euro: '€',
  pound: '£',
  cent: '¢',
  times: '×',
  divide: '÷',
  frac12: '½',
  frac14: '¼',
  frac34: '¾',
  zwnj: '',
  zwj: '',
  shy: '',
};

const ENTITY_PATTERN = /&(#x?[0-9a-f]+|[a-z][a-z0-9]{1,9});/gi;

const fromCodePoint = (code) => {
  if (!Number.isFinite(code) || code < 0 || code > 0x10ffff) return '';
  try {
    return String.fromCodePoint(code);
  } catch {
    return '';
  }
};

/**
 * Decode HTML entities. Gmail sometimes double-encodes (`&amp;#39;`), so we run a
 * few passes until the string stops changing.
 */
export const decodeEmailEntities = (value) => {
  if (!value) return '';
  let text = String(value);
  for (let pass = 0; pass < 3; pass += 1) {
    if (!ENTITY_PATTERN.test(text)) break;
    ENTITY_PATTERN.lastIndex = 0;
    const decoded = text.replace(ENTITY_PATTERN, (match, entity) => {
      const token = entity.toLowerCase();
      if (token.startsWith('#x')) return fromCodePoint(parseInt(entity.slice(2), 16)) || match;
      if (token.startsWith('#')) return fromCodePoint(parseInt(entity.slice(1), 10)) || match;
      return Object.prototype.hasOwnProperty.call(NAMED_ENTITIES, token) ? NAMED_ENTITIES[token] : match;
    });
    if (decoded === text) break;
    text = decoded;
  }
  ENTITY_PATTERN.lastIndex = 0;
  return text;
};

const tidyWhitespace = (text) =>
  String(text || '')
    .replace(/\r\n?/g, '\n')
    .replace(/ /g, ' ')
    .replace(/[ \t]+$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

/** Flatten an HTML email body into readable plain text. */
export const htmlToPlainText = (html) => {
  if (!html) return '';
  const withBreaks = String(html)
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<(script|style|head)[\s\S]*?<\/\1>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|tr|li|h[1-6]|blockquote|table)>/gi, '\n')
    .replace(/<\/t[dh]>/gi, '  ')
    .replace(/<li[^>]*>/gi, '• ')
    .replace(/<[^>]+>/g, '');
  return tidyWhitespace(decodeEmailEntities(withBreaks));
};

const QUOTE_DIVIDERS = [
  /^-{2,}\s*(original message|forwarded message)\s*-{2,}$/i,
  /^_{10,}$/,
  /^-{5,}$/,
];

/**
 * Split a body into the part the sender actually typed and the quoted history
 * underneath it ("On Mon, Aug 10 … wrote:", Outlook's `From:/Sent:/To:` block,
 * `>` quoting, etc.).
 */
export const splitQuotedReply = (text) => {
  const source = tidyWhitespace(text);
  if (!source) return { body: '', quoted: '' };

  const lines = source.split('\n');
  let cutIndex = -1;

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i].trim();
    if (!line) continue;

    if (line.startsWith('>')) {
      cutIndex = i;
      break;
    }
    if (QUOTE_DIVIDERS.some((pattern) => pattern.test(line))) {
      cutIndex = i;
      break;
    }
    // Outlook quotes start with a `From:` header followed by Sent:/Date:/To:
    if (/^from:\s/i.test(line)) {
      const window = lines.slice(i + 1, i + 4).join('\n');
      if (/^\s*(sent|date|to|subject):\s/im.test(window)) {
        cutIndex = i;
        break;
      }
    }
    // Gmail quotes start with "On <date> <person> wrote:" — often wrapped over 2-3 lines
    if (/^on\s/i.test(line)) {
      const window = lines.slice(i, i + 3).join(' ');
      if (/^on\s[\s\S]{5,400}?\bwrote:/i.test(window)) {
        cutIndex = i;
        break;
      }
    }
  }

  if (cutIndex === -1) return { body: source, quoted: '' };

  const body = lines.slice(0, cutIndex).join('\n').trim();
  const quoted = lines.slice(cutIndex).join('\n').trim();
  // A reply with nothing above the quote is still worth reading in full.
  if (!body) return { body: quoted, quoted: '' };
  return { body, quoted };
};

/**
 * Drop the placeholders mail clients leave in plain-text bodies where an inline
 * image or attachment sat — Outlook's `[cid:image001.png@01DB2C3A.5F1E8B20]`,
 * Gmail's `[image: photo.jpg]` — since the files themselves are listed under the message.
 */
export const stripAttachmentPlaceholders = (text) =>
  String(text || '')
    .replace(/\[cid:[^\]\s]+\]/gi, '')
    .replace(/\[image:[^\]\n]*\]/gi, '')
    .replace(/[ \t]+$/gm, '');

/** Best readable body for a message, with the quoted history separated out. */
export const getReadableEmailBody = (email) => {
  const plain = email?.bodyText
    ? tidyWhitespace(stripAttachmentPlaceholders(decodeEmailEntities(email.bodyText)))
    : '';
  const fromHtml = !plain && email?.bodyHtml ? htmlToPlainText(email.bodyHtml) : '';
  const fallback = tidyWhitespace(decodeEmailEntities(email?.snippet || ''));
  const text = plain || fromHtml || fallback;
  if (!text) return { body: '', quoted: '' };
  return splitQuotedReply(text);
};

/** Short one-line preview (used on collapsed rows). */
export const getEmailPreview = (email) => {
  const { body } = getReadableEmailBody(email);
  const source = body || tidyWhitespace(decodeEmailEntities(email?.snippet || ''));
  return source.replace(/\s+/g, ' ').trim();
};

/** Split a To/Cc header into individual addresses without breaking `"Doe, John" <j@x>`. */
const splitAddressList = (raw) => {
  const value = String(raw || '');
  const parts = [];
  let current = '';
  let inQuote = false;
  let inAngle = false;

  for (const char of value) {
    if (char === '"') inQuote = !inQuote;
    else if (char === '<') inAngle = true;
    else if (char === '>') inAngle = false;

    if (char === ',' && !inQuote && !inAngle) {
      parts.push(current);
      current = '';
      continue;
    }
    current += char;
  }
  parts.push(current);
  return parts.map((part) => part.trim()).filter(Boolean);
};

/** `"Ken Combs" <ken@x.com>` → `{ name: 'Ken Combs', email: 'ken@x.com' }` */
export const parseEmailAddress = (raw) => {
  const value = decodeEmailEntities(String(raw || '')).trim();
  if (!value) return { name: '', email: '', label: '' };

  const angled = value.match(/^(.*?)<([^>]+)>\s*$/);
  if (angled) {
    const name = angled[1].replace(/["']/g, '').trim();
    const email = angled[2].trim();
    return { name: name || email, email: email.toLowerCase(), label: value };
  }

  const bare = value.replace(/["']/g, '').trim();
  return { name: bare, email: bare.toLowerCase(), label: bare };
};

export const parseEmailAddressList = (raw) => splitAddressList(raw).map(parseEmailAddress);

export const getInitials = (name) => {
  const parts = String(name || '')
    .replace(/[^a-zA-Z\s]/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

/** Strip Re:/Fwd: prefixes so replies land in the same thread. */
export const normalizeSubject = (subject) => {
  let value = decodeEmailEntities(String(subject || '')).trim();
  if (value === '(No Subject)') value = '';
  let previous;
  do {
    previous = value;
    value = value.replace(/^\s*(re|fw|fwd|aw|antw|sv|vs)\s*(\[\d+\])?\s*:\s*/i, '').trim();
  } while (value !== previous);
  return value;
};

export const toEmailDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value?.toDate === 'function') {
    const converted = value.toDate();
    return Number.isNaN(converted?.getTime?.()) ? null : converted;
  }
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const addressesFor = (email) => {
  const list = [parseEmailAddress(email?.from), ...parseEmailAddressList(email?.to), ...parseEmailAddressList(email?.cc)];
  return new Set(list.map((entry) => entry.email).filter(Boolean));
};

/**
 * The same message pulled from two internal mailboxes comes back with different
 * Gmail ids, so identity is sender + subject + minute.
 */
const dedupeKey = (email) => {
  const date = toEmailDate(email?.date);
  const minute = date ? Math.floor(date.getTime() / 60000) : 'no-date';
  const from = parseEmailAddress(email?.from).email;
  return `${from}|${minute}|${normalizeSubject(email?.subject).toLowerCase()}`;
};

/**
 * Group raw messages into conversations.
 * @param {Array} emails raw Gmail messages (may contain cross-mailbox duplicates)
 * @returns {Array} threads, newest first, each with messages oldest → newest
 */
export const buildEmailThreads = (emails = []) => {
  // 1. Collapse duplicates fetched from multiple internal mailboxes.
  const unique = new Map();
  emails.forEach((email) => {
    if (!email) return;
    const key = dedupeKey(email);
    const existing = unique.get(key);
    if (!existing) {
      unique.set(key, { ...email, fetchedByAll: email.fetchedBy ? [email.fetchedBy] : [] });
      return;
    }
    // Keep the copy with the richest body, but remember everyone who surfaced it.
    const existingLength = (existing.bodyText || existing.bodyHtml || '').length;
    const candidateLength = (email.bodyText || email.bodyHtml || '').length;
    const fetchedByAll = [...existing.fetchedByAll];
    if (email.fetchedBy && !fetchedByAll.some((user) => user.id === email.fetchedBy.id)) {
      fetchedByAll.push(email.fetchedBy);
    }
    const winner = candidateLength > existingLength ? { ...email } : existing;
    unique.set(key, { ...winner, fetchedByAll });
  });

  // 2. Group by Gmail thread id (per-mailbox), then merge groups that are clearly
  //    the same conversation seen from a different inbox.
  const groups = [];
  const byThreadId = new Map();

  [...unique.values()].forEach((email) => {
    const subjectKey = normalizeSubject(email.subject).toLowerCase();
    const threadKey = email.threadId ? `id:${email.threadId}` : `subject:${subjectKey}|${dedupeKey(email)}`;
    let group = byThreadId.get(threadKey);
    if (!group) {
      group = { subjectKey, messages: [], addresses: new Set() };
      byThreadId.set(threadKey, group);
      groups.push(group);
    }
    group.messages.push(email);
    addressesFor(email).forEach((address) => group.addresses.add(address));
  });

  const merged = [];
  groups.forEach((group) => {
    const match = merged.find((candidate) => {
      if (candidate.subjectKey !== group.subjectKey) return false;
      return [...group.addresses].some((address) => candidate.addresses.has(address));
    });
    if (match) {
      match.messages.push(...group.messages);
      group.addresses.forEach((address) => match.addresses.add(address));
      return;
    }
    merged.push({ ...group, addresses: new Set(group.addresses) });
  });

  // 3. Shape each conversation for rendering.
  return merged
    .map((group) => {
      const messages = group.messages
        .map((email) => ({ ...email, dateValue: toEmailDate(email.date) }))
        .sort((a, b) => (a.dateValue?.getTime() || 0) - (b.dateValue?.getTime() || 0));

      const latest = messages[messages.length - 1];
      const first = messages[0];
      const subject =
        normalizeSubject(first?.subject) || normalizeSubject(latest?.subject) || '(No Subject)';

      const participants = [];
      messages.forEach((message) => {
        const sender = parseEmailAddress(message.from);
        if (!sender.email) return;
        if (!participants.some((person) => person.email === sender.email)) participants.push(sender);
      });

      return {
        id: latest?.threadId || latest?.id || subject,
        subject,
        messages,
        participants,
        messageCount: messages.length,
        unreadCount: messages.filter((message) => message.isRead === false).length,
        latest,
        latestDate: latest?.dateValue || null,
        htmlLink: latest?.htmlLink || first?.htmlLink || '',
      };
    })
    .sort((a, b) => (b.latestDate?.getTime() || 0) - (a.latestDate?.getTime() || 0));
};

/** Does this message involve the lead/client as sender ('from') or recipient ('to')? */
export const messageMatchesDirection = (email, counterpartEmail, direction) => {
  if (direction === 'all' || !direction) return true;
  const counterpart = String(counterpartEmail || '').toLowerCase().trim();
  if (!counterpart) return true;

  if (direction === 'from') {
    return parseEmailAddress(email?.from).email.includes(counterpart);
  }
  if (direction === 'to') {
    const recipients = [...parseEmailAddressList(email?.to), ...parseEmailAddressList(email?.cc)].map((entry) => entry.email);
    const legacy = Array.isArray(email?.recipients) ? email.recipients.map((value) => String(value).toLowerCase()) : [];
    return [...recipients, ...legacy].some((value) => value.includes(counterpart));
  }
  return true;
};

export const isFromCounterpart = (email, counterpartEmail) => {
  const counterpart = String(counterpartEmail || '').toLowerCase().trim();
  if (!counterpart) return false;
  return parseEmailAddress(email?.from).email.includes(counterpart);
};
